/**
 * 埋点上报：支持 page_view / search / click / convert
 * 优先使用 sendBeacon，确保跳转离开时仍能发送
 */
import { REACT_APP_API_BASE_URL } from './apiBase';

console.log("Current API Base:", process.env.REACT_APP_API_BASE_URL);

/** 埋点强制使用后端完整地址，不依赖相对路径或 proxy */
const TRACK_ENDPOINT =
  REACT_APP_API_BASE_URL !== ''
    ? `${REACT_APP_API_BASE_URL.replace(/\/$/, '')}/api/logs/event`
    : '/api/logs/event';
const DEVICE_ID_KEY = 'track_device_id';
const SESSION_ID_KEY = 'track_session_id';

export type TrackEventType = 'page_view' | 'search' | 'click' | 'convert';

export interface TrackEventParams {
  event_type: TrackEventType;
  target_id?: string;
  search_query?: string;
  position?: number;
}

function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * 从 localStorage 获取或创建唯一设备 ID，作为 user_id 使用
 */
export function getOrCreateDeviceId(): string {
  if (typeof window === 'undefined') return '';
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id || !id.trim()) {
      id = generateId();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return generateId();
  }
}

/** target_id 必须是有效 ID，不能是 URL 或路径字符串 */
function isValidTargetId(value: string): boolean {
  const s = value.trim();
  if (s.length === 0) return false;
  if (/^https?:\/\//i.test(s)) return false;
  if (/^\//.test(s)) return false;
  return true;
}

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id || !id.trim()) {
      id = generateId();
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return generateId();
  }
}

if (typeof window !== 'undefined') {
  getOrCreateSessionId();
}

function getPayload(params: TrackEventParams): string {
  const body: Record<string, unknown> = {
    event_type: params.event_type,
    user_id: getOrCreateDeviceId(),
    session_id: getOrCreateSessionId(),
    referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
  };

  body.page_url = typeof window !== 'undefined' ? window.location.href : '';
  if (params.event_type === 'page_view') {
    // page_view 不发送 target_id，避免把当前 URL 路径当 ID 上报
  } else {
    // 仅非 page_view 时考虑 target_id，且必须是有效 ID（不能是 URL 或路径）
    const raw = params.target_id;
    if (raw != null && raw !== '' && isValidTargetId(raw)) {
      body.target_id = raw;
    }
    if (params.event_type === 'click' && (params.target_id == null || params.target_id === '')) {
      body.target_id = '';
    }
  }

  if (params.search_query != null && params.search_query !== '') {
    body.search_query = params.search_query;
  }
  if (params.event_type === 'search' && (params.search_query == null || params.search_query === '')) {
    body.search_query = '';
  }

  if (params.position != null) {
    body.position = params.position;
  }

  return JSON.stringify(body);
}

/**
 * 上报埋点事件。
 * 开发环境可设置 REACT_APP_TRACK_ENABLED=false 关闭上报。
 */
export function trackEvent(params: TrackEventParams): void {
  if (typeof window === 'undefined') return;
  if (process.env.REACT_APP_TRACK_ENABLED === 'false') return;
  const url = TRACK_ENDPOINT;
  const payload = getPayload(params);

  // 统一使用 fetch：sendBeacon 在页面不跳转时（如 Canvas）可能被延迟或丢弃，fetch+keepalive 更可靠
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => {});
}
